import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import Image from 'next/image'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { formatUserName } from 'utils/format-user-name'
import { VerifiedPremiumRibbon } from '@web/app/[locale]/clubhouse/[slug]/_components/verified-premium-ribbon'
import type { PublicUserProfile } from 'codegen/types'
import { FollowButton } from '@web/app/[locale]/clubhouse/[slug]/members/_components/follow-button'
import { isMobileUserAgent } from '@repo/web-utils/is-mobile-user-agent'
import { socialUserFollowersQueryRootKey, socialUserFollowingQueryRootKey } from '@web/gen/crafter'
import { SkeletonItem } from 'components/skeleton-item'

type PublicUserProfileWithoutLocation = Omit<PublicUserProfile, 'location'>

export function UserCard({
  user,
  ownUserID,
  slug,
}: {
  user: PublicUserProfileWithoutLocation
  ownUserID?: string
  slug?: string
}) {
  const isMobile = isMobileUserAgent()
  const queryClient = useQueryClient()

  const handleFollowChange = () => {
    queryClient.invalidateQueries({
      queryKey: [`${socialUserFollowersQueryRootKey}.Infinite`],
    })
    queryClient.invalidateQueries({
      queryKey: [`${socialUserFollowingQueryRootKey}.Infinite`],
    })
    queryClient.invalidateQueries({ queryKey: [socialUserFollowersQueryRootKey] })
    queryClient.invalidateQueries({ queryKey: [socialUserFollowingQueryRootKey] })
  }

  const isOwn = ownUserID === user.id

  if (user.slug === slug) {
    return null
  }

  return (
    <div
      className="rounded-[12px] bg-cf-white p-4 flex flex-col sm:items-center sm:justify-between border border-[#1313151F] sm:h-[230px]"
      id={user.id}
    >
      {isMobile ? (
        <div className="flex w-full gap-3 items-start">
          <div className="h-16 w-16 flex-shrink-0">
            {user.avatarUrl ? (
              <Link
                href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${user.slug}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-16 w-16"
              >
                <Image
                  src={user.avatarUrl}
                  alt="User Avatar"
                  width={64}
                  height={64}
                  className="h-16 w-16 rounded-full border border-[#1313151F] object-cover"
                />
              </Link>
            ) : null}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex w-full mt-2 px-1">
              <Link
                href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${user.slug}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="block max-w-[280px]"
              >
                <span className="text-16 font-medium hover:underline break-words line-clamp-2">
                  {formatUserName(user?.firstName, user?.lastName, 'User')}{' '}
                  <span className="inline-block align-middle">
                    <VerifiedPremiumRibbon user={user as PublicUserProfile} />
                  </span>
                </span>
              </Link>
            </div>

            <p className="mt-1 text-14 text-[#4B5563] line-clamp-2 break-words">
              {user.superSkill}
            </p>

            {!isOwn ? (
              <div className="mt-2">
                <FollowButton
                  size="small"
                  following={user?.following}
                  userId={user.id}
                  onFollowSuccess={handleFollowChange}
                />
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          <div className="flex sm:flex-col items-center">
            <div className="h-[80px] w-[80px]">
              {user.avatarUrl ? (
                <Link
                  href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${user.slug}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image
                    src={user.avatarUrl}
                    alt="logo"
                    width={80}
                    height={80}
                    className="overflow-hidden rounded-full border border-[#1313151F] object-cover"
                  />
                </Link>
              ) : null}
            </div>
            <div className="flex justify-center w-full mt-2 px-1">
              <Link
                href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${user.slug}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="block max-w-[150px]"
              >
                <span className="text-16 font-medium hover:underline text-center break-words line-clamp-2">
                  {formatUserName(user?.firstName, user?.lastName, 'User')}{' '}
                  <span className="inline-block align-middle">
                    <VerifiedPremiumRibbon user={user as PublicUserProfile} />
                  </span>
                </span>
              </Link>
            </div>

            <p className="text-[#4B5563] line-clamp-2 max-h-12 overflow-hidden text-ellipsis pt-1">
              {user.superSkill}
            </p>
          </div>
          {!isOwn ? (
            <div className="mt-4 w-full">
              <FollowButton
                following={user?.following}
                userId={user.id}
                className="w-full"
                onFollowSuccess={handleFollowChange}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

export function UserCardSkeleton() {
  return (
    <div className="rounded-[12px] bg-cf-white p-4 flex flex-col sm:items-center sm:justify-between border border-[#1313151F] sm:h-[230px]">
      {/* Desktop layout */}
      <div className="hidden sm:flex sm:flex-col items-center w-full">
        {/* Avatar */}
        <SkeletonItem className="h-[80px] w-[80px] rounded-full" />

        {/* Name + subtitle lines */}
        <div className="flex flex-col items-center w-full mt-3 px-2 gap-2">
          <SkeletonItem className="h-4 w-4/5 rounded" />
          <SkeletonItem className="h-3 w-3/5 rounded" />
        </div>

        {/* Follow button skeleton */}
        <SkeletonItem className="mt-4 h-9 w-full rounded" />
      </div>

      {/* Mobile layout */}
      <div className="flex sm:hidden w-full gap-3 items-start">
        {/* Avatar */}
        <SkeletonItem className="h-16 w-16 rounded-full flex-shrink-0" />

        {/* Text + button */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <SkeletonItem className="h-4 w-3/4 rounded" />
          <SkeletonItem className="h-3 w-full rounded" />
          <SkeletonItem className="h-8 w-24 rounded mt-1" />
        </div>
      </div>
    </div>
  )
}
